import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { Server } from 'lucide-react';
import {
  Button,
  Card,
  StatusBadge,
  NotificationCard,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormField,
  useToast,
} from '@sighthost/ui';
import { domainService, type Domain } from '@/domains/domains-registry';

// Canonical default NS per CLAUDE.md §4.9 + system_setting.domains_default_nameservers
// (updated 2026-06-02 to cpns.host/cpns.zone). cPanel Cloud DNS Anycast cluster.
// Used both to DETECT whether the domain is on SightHost defaults (Reset
// button + "Using defaults" badge) and to SET the values when the customer
// clicks "Reset to SightHost defaults".
const SIGHTHOST_DEFAULT_NS = ['ns1.cpns.host', 'ns2.cpns.zone', 'ns3.cpns.host', 'ns4.cpns.zone'];

export default function DomainNameserversTab() {
  const domain = useOutletContext<Domain>();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const usingDefaults = arraysEqual(
    [...domain.nameservers].sort(),
    [...SIGHTHOST_DEFAULT_NS].sort(),
  );

  async function handleSave(nameservers: string[]) {
    setSaving(true);
    try {
      await domainService.updateNameservers(domain.id, nameservers);
      toast({
        tone: 'success',
        title: 'Nameservers updated',
        body: 'Registry change accepted. DNS may take up to 24h to propagate.',
      });
      setEditOpen(false);
      // Soft refresh — Domain detail is typically driven by react-query;
      // a window reload guarantees the new NS list shows.
      window.setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'NS update failed.';
      toast({ tone: 'danger', title: 'Could not update nameservers', body: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card interactive={false}>
        <header className="flex items-center justify-between border-b border-border px-6 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink">Nameservers</h3>
            <p className="text-xs text-muted-foreground">
              {usingDefaults
                ? 'Using SightHost defaults — DNS hosted on our nameservers.'
                : 'Using custom nameservers — DNS is hosted elsewhere.'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
        </header>

        <ul className="divide-y divide-border">
          {domain.nameservers.map((ns, i) => (
            <li key={ns} className="flex items-center gap-3 px-6 py-3 text-sm">
              <StatusBadge tone="info">NS{i + 1}</StatusBadge>
              <Server className="h-4 w-4 text-muted-foreground" />
              <span className="text-ink tabular-nums">{ns}</span>
            </li>
          ))}
          {domain.nameservers.length === 0 && (
            <li className="px-6 py-6 text-sm text-muted-foreground">
              No nameservers configured.
            </li>
          )}
        </ul>
      </Card>

      {!usingDefaults && (
        <NotificationCard
          tone="warning"
          title="Custom nameservers in use"
          body="DNS records configured in this portal will not take effect until you switch back to SightHost nameservers."
        />
      )}

      <NsEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        domain={domain}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
}

function NsEditModal({
  open,
  onClose,
  domain,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  domain: Domain;
  onSave: (ns: string[]) => Promise<void>;
  saving: boolean;
}) {
  const seed = (domain.nameservers ?? []).slice(0, 5);
  while (seed.length < 5) seed.push('');
  const [ns, setNs] = useState<string[]>(seed);

  function update(i: number, v: string) {
    const next = [...ns];
    next[i] = v;
    setNs(next);
  }

  function reset() {
    // Pad to 5 slots with empties if defaults are < 5; truncate if > 5.
    const next = [...SIGHTHOST_DEFAULT_NS, '', '', '', '', ''].slice(0, 5);
    setNs(next);
  }

  async function submit() {
    const cleaned = ns.map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (cleaned.length === 0) return;
    await onSave(cleaned);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={`Update nameservers · ${domain.name}`}
      description="Provide 1–5 nameservers. Empty rows are ignored. Switching away from SightHost defaults disables in-portal DNS hosting for this domain."
      size="wide"
    >
      <ModalBody>
        <div className="space-y-3">
          {ns.map((v, i) => (
            <FormField key={i} label={`NS${i + 1}`} htmlFor={`ns-${i}`}>
              <Input
                id={`ns-${i}`}
                value={v}
                onChange={(e) => update(i, e.target.value)}
                placeholder={SIGHTHOST_DEFAULT_NS[i] ?? 'optional'}
                disabled={saving}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                className="text-sm tabular-nums"
              />
            </FormField>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={reset} disabled={saving}>
          Reset to SightHost defaults
        </Button>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={submit} loading={saving} disabled={saving}>
          Save nameservers
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function arraysEqual(a: string[], b: string[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
